<template>
  <v-dialog :model-value="modelValue" max-width="860" @update:model-value="handleDialogToggle">
    <v-card>
      <v-card-title class="text-h6">{{ $t("ncrm.integrations.appSyncs.resolve.title") }}</v-card-title>
      <v-card-text class="pt-2">
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{
            $t("ncrm.integrations.appSyncs.resolve.subtitle", {
              company: item?.mitp_company_name || "-",
              idno: item?.mitp_idno || "-",
            })
          }}
        </div>

        <v-alert type="info" class="mb-4">
          {{ $t("ncrm.integrations.appSyncs.resolve.rules") }}
        </v-alert>

        <v-text-field
          v-model="searchQuery"
          :label="$t('ncrm.integrations.appSyncs.resolve.searchLabel')"
          prepend-inner-icon="mdi-magnify"
          hide-details
          class="mb-4"
          @update:model-value="scheduleSearch"
        />

        <div v-if="candidatesLoading" class="text-body-2 text-medium-emphasis">
          {{ $t("ncrm.integrations.appSyncs.resolve.loading") }}
        </div>

        <v-list
          v-else-if="sortedCandidates.length"
          lines="two"
          density="comfortable"
          rounded="lg"
          border
          class="ncrm-mitp-resolve-dialog__list"
        >
          <v-list-item
            v-for="candidate in sortedCandidates"
            :key="candidate.id"
            :active="selectedCompanyId === Number(candidate.id)"
            @click="selectCandidate(candidate)"
          >
            <template #prepend>
              <v-icon :color="selectedCompanyId === Number(candidate.id) ? 'primary' : 'medium-emphasis'">
                {{ selectedCompanyId === Number(candidate.id) ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
              </v-icon>
            </template>
            <v-list-item-title>{{ candidate.name || "-" }}</v-list-item-title>
            <v-list-item-subtitle>{{ formatCandidateSubtitle(candidate) }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>

        <v-alert v-else type="info">
          {{ $t("ncrm.integrations.appSyncs.resolve.empty") }}
        </v-alert>

        <div v-if="errorMessage" class="text-body-2 text-error mt-3">
          {{ errorMessage }}
        </div>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-btn variant="text" @click="closeDialog">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-spacer />
        <v-btn color="primary" :loading="saveLoading" :disabled="!selectedCompanyId" @click="submitResolve">
          {{ $t("ncrm.integrations.appSyncs.resolve.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import {
  normalizeMitpCompanySearchQuery,
  sortMitpCompanyCandidates,
} from "@/Utile/ncrmMitpSync.mjs";

export default {
  name: "NcrmMitpResolveUnmatchedDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    jobId: {
      type: [Number, String],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ["error", "resolved", "update:modelValue"],
  data() {
    return {
      candidatesLoading: false,
      candidates: [],
      errorMessage: "",
      saveLoading: false,
      searchDebounceId: null,
      searchQuery: "",
      selectedCompanyId: null,
    };
  },
  computed: {
    sortedCandidates() {
      return sortMitpCompanyCandidates(this.item?.mitp_company_name, this.candidates);
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.openDialog();
        return;
      }
      this.resetState();
    },
  },
  methods: {
    handleDialogToggle(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    openDialog() {
      this.errorMessage = "";
      this.selectedCompanyId = null;
      this.searchQuery = normalizeMitpCompanySearchQuery(this.item?.mitp_company_name);
      this.fetchCandidates({ force: true });
    },
    closeDialog() {
      this.$emit("update:modelValue", false);
    },
    resetState() {
      this.candidates = [];
      this.candidatesLoading = false;
      this.errorMessage = "";
      this.saveLoading = false;
      this.selectedCompanyId = null;
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
        this.searchDebounceId = null;
      }
    },
    scheduleSearch() {
      if (this.searchDebounceId) clearTimeout(this.searchDebounceId);
      this.searchDebounceId = setTimeout(() => {
        this.fetchCandidates({ force: true });
      }, 260);
    },
    async fetchCandidates({ force = false } = {}) {
      if (!this.modelValue || this.candidatesLoading) return;
      if (!force && this.candidates.length) return;
      this.candidatesLoading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/mitp/companies", {
          params: {
            page: 1,
            perPage: 20,
            search: String(this.searchQuery || "").trim() || undefined,
          },
        });
        this.candidates = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.candidates = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.resolve.loadCandidatesError");
        this.$emit("error", this.errorMessage);
      } finally {
        this.candidatesLoading = false;
      }
    },
    selectCandidate(candidate) {
      this.selectedCompanyId = Number(candidate?.id || 0) || null;
      this.errorMessage = "";
    },
    formatCandidateSubtitle(candidate) {
      const parts = [
        candidate?.fiscal_code
          ? `${this.$t("ncrm.integrations.appSyncs.resolve.fiscalCodePrefix")}: ${candidate.fiscal_code}`
          : this.$t("ncrm.integrations.appSyncs.resolve.noFiscalCode"),
        candidate?.business_category,
        candidate?.email_primary,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      return parts.join(" • ");
    },
    async submitResolve() {
      if (!this.jobId || !this.item?.id || !this.selectedCompanyId || this.saveLoading) return;
      this.saveLoading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mitp/jobs/${this.jobId}/items/${this.item.id}/resolve`,
          {
            ncrm_company_id: this.selectedCompanyId,
          }
        );
        this.$emit("resolved", data);
        this.closeDialog();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.resolve.submitError");
        this.$emit("error", this.errorMessage);
      } finally {
        this.saveLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mitp-resolve-dialog__list {
  max-height: min(50vh, 420px);
  overflow-y: auto;
}
</style>
